import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../utils/dateFormat';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  User,
  Shield,
  Calendar,
  Globe,
  Key,
  Smartphone,
  Clock,
  History,
  Monitor,
  Trash2,
  Plus,
  Copy,
  Eye,
  EyeOff,
  Check,
  AlertTriangle,
  Loader2,
  Mail,
  Phone,
  Lock,
  Settings,
  ChevronRight
} from 'lucide-react';
import { cn } from '../lib/utils';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'UTC', label: 'UTC' },
];

const DATE_FORMATS = [
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (International)' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
];

const TIME_FORMATS = [
  { value: '12h', label: '12-hour (1:00 PM)' },
  { value: '24h', label: '24-hour (13:00)' },
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
];

const CURRENCIES = [
  { value: 'USD', label: 'US Dollar ($)' },
  { value: 'EUR', label: 'Euro (€)' },
  { value: 'GBP', label: 'British Pound (£)' },
];

const MEASUREMENT_UNITS = [
  { value: 'imperial', label: 'Imperial (miles, pounds)' },
  { value: 'metric', label: 'Metric (kilometers, kilograms)' },
];

const DAYS_OF_WEEK = [
  { value: 'monday', label: 'Mon' },
  { value: 'tuesday', label: 'Tue' },
  { value: 'wednesday', label: 'Wed' },
  { value: 'thursday', label: 'Thu' },
  { value: 'friday', label: 'Fri' },
  { value: 'saturday', label: 'Sat' },
  { value: 'sunday', label: 'Sun' },
];

const APPOINTMENT_TYPES = [
  { value: 'initial_assessment', label: 'Initial Assessment' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'case_review', label: 'Case Review' },
  { value: 'home_visit', label: 'Home Visit' },
  { value: 'phone_call', label: 'Phone Call' },
  { value: 'video_call', label: 'Video Call' },
];

const CALENDAR_COLORS = [
  '#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#6366f1'
];

const ProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [preferences, setPreferences] = useState(null);
  const [security, setSecurity] = useState(null);
  const [activeTab, setActiveTab] = useState('account');
  
  // Password change state
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [passwordErrors, setPasswordErrors] = useState([]);
  
  // API Token state
  const [showTokenDialog, setShowTokenDialog] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [createdToken, setCreatedToken] = useState(null);
  const [tokenToDelete, setTokenToDelete] = useState(null);

  const token = localStorage.getItem('myhelp_token');

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(res.data.user);
      setPreferences(res.data.preferences);
      setSecurity(res.data.security);
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (updates) => {
    try {
      setSaving(true);
      await axios.put(`${API_BASE}/api/profile`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully');
      fetchProfile();
      if (refreshUser) refreshUser();
    } catch (err) {
      console.error('Failed to update profile:', err);
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePreferences = async () => {
    try {
      setSaving(true);
      await axios.put(`${API_BASE}/api/profile/preferences`, preferences, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Preferences saved successfully');
    } catch (err) {
      console.error('Failed to update preferences:', err);
      toast.error(err.response?.data?.detail || 'Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('At least 8 characters');
    if (!/[A-Z]/.test(password)) errors.push('At least 1 uppercase letter');
    if (!/\d/.test(password)) errors.push('At least 1 number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push('At least 1 special character');
    return errors;
  };

  const handlePasswordChange = async () => {
    const errors = validatePassword(passwordForm.new_password);
    if (errors.length > 0) {
      setPasswordErrors(errors);
      return;
    }
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${API_BASE}/api/profile/change-password`, passwordForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Password changed successfully');
      setShowPasswordDialog(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setPasswordErrors([]);
      fetchProfile();
    } catch (err) {
      console.error('Failed to change password:', err);
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateToken = async () => {
    if (!newTokenName.trim()) {
      toast.error('Please enter a token name');
      return;
    }

    try {
      setSaving(true);
      const res = await axios.post(`${API_BASE}/api/profile/api-tokens`, 
        { name: newTokenName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCreatedToken(res.data);
      setNewTokenName('');
      fetchProfile();
    } catch (err) {
      console.error('Failed to create token:', err);
      toast.error(err.response?.data?.detail || 'Failed to create token');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteToken = async () => {
    if (!tokenToDelete) return;

    try {
      await axios.delete(`${API_BASE}/api/profile/api-tokens/${tokenToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('API token revoked');
      setTokenToDelete(null);
      fetchProfile();
    } catch (err) {
      console.error('Failed to delete token:', err);
      toast.error(err.response?.data?.detail || 'Failed to revoke token');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return formatDateTime(dateStr);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Profile Settings</h1>
          <p className="text-muted-foreground">Manage your account settings and preferences</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="account" className="gap-2">
            <User className="w-4 h-4" />
            <span className="hidden sm:inline">Account</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2">
            <Shield className="w-4 h-4" />
            <span className="hidden sm:inline">Security</span>
          </TabsTrigger>
          <TabsTrigger value="calendar" className="gap-2">
            <Calendar className="w-4 h-4" />
            <span className="hidden sm:inline">Calendar</span>
          </TabsTrigger>
          <TabsTrigger value="regional" className="gap-2">
            <Globe className="w-4 h-4" />
            <span className="hidden sm:inline">Regional</span>
          </TabsTrigger>
        </TabsList>

        {/* Account Tab */}
        <TabsContent value="account" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>Your basic account information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <Input 
                    value={profile?.name || ''} 
                    onChange={(e) => setProfile({...profile, name: e.target.value})}
                    data-testid="profile-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={profile?.email || ''} disabled className="bg-muted" />
                  <p className="text-xs text-muted-foreground">Contact admin to change email</p>
                </div>
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <Input 
                    value={profile?.phone_number || ''} 
                    onChange={(e) => setProfile({...profile, phone_number: e.target.value})}
                    placeholder="(555) 123-4567"
                    data-testid="profile-phone"
                  />
                  <p className="text-xs text-muted-foreground">Required for RingOut calls</p>
                </div>
                <div className="space-y-2">
                  <Label>M365 Email</Label>
                  <Input 
                    value={profile?.m365_email || ''} 
                    onChange={(e) => setProfile({...profile, m365_email: e.target.value})}
                    placeholder="user@organization.com"
                    data-testid="profile-m365"
                  />
                  <p className="text-xs text-muted-foreground">For calendar sync</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4 pt-4">
                <div className="flex-1">
                  <p className="text-sm font-medium">Role</p>
                  <p className="text-sm text-muted-foreground">{profile?.role_display_name}</p>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Account Status</p>
                  <Badge variant={profile?.is_active ? "default" : "destructive"}>
                    {profile?.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Member Since</p>
                  <p className="text-sm text-muted-foreground">{formatDate(profile?.created_at)}</p>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={() => handleUpdateProfile({ 
                    name: profile.name, 
                    phone_number: profile.phone_number,
                    m365_email: profile.m365_email 
                  })}
                  disabled={saving}
                  data-testid="save-profile-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Recovery Options</CardTitle>
              <CardDescription>Backup contact methods for account recovery</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Recovery Email</Label>
                  <Input 
                    value={profile?.recovery_email || ''} 
                    onChange={(e) => setProfile({...profile, recovery_email: e.target.value})}
                    placeholder="backup@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Recovery Phone</Label>
                  <Input 
                    value={profile?.recovery_phone || ''} 
                    onChange={(e) => setProfile({...profile, recovery_phone: e.target.value})}
                    placeholder="(555) 987-6543"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button 
                  onClick={() => handleUpdateProfile({ 
                    recovery_email: profile.recovery_email, 
                    recovery_phone: profile.recovery_phone 
                  })}
                  disabled={saving}
                  variant="outline"
                >
                  Update Recovery Options
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Password
              </CardTitle>
              <CardDescription>Manage your password and authentication</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Password</p>
                  <p className="text-sm text-muted-foreground">
                    Last changed: {security?.password_last_changed ? formatDate(security.password_last_changed) : 'Never'}
                  </p>
                </div>
                <Button onClick={() => setShowPasswordDialog(true)} data-testid="change-password-btn">
                  Change Password
                </Button>
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Two-Factor Authentication</p>
                  <p className="text-sm text-muted-foreground">
                    Add an extra layer of security to your account
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={security?.two_factor_enabled ? "default" : "secondary"}>
                    {security?.two_factor_enabled ? "Enabled" : "Disabled"}
                  </Badge>
                  <Button variant="outline" size="sm" disabled>
                    {security?.two_factor_enabled ? "Disable" : "Enable"} 2FA
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Login History
              </CardTitle>
              <CardDescription>Recent sign-in activity on your account</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-48">
                {security?.login_history?.length > 0 ? (
                  <div className="space-y-2">
                    {security.login_history.map((login, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div className="flex items-center gap-3">
                          <Monitor className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium">{login.user_agent || 'Unknown Device'}</p>
                            <p className="text-xs text-muted-foreground">{login.ip_address || 'Unknown IP'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {login.success ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <AlertTriangle className="w-4 h-4 text-red-500" />
                          )}
                          <span className="text-xs text-muted-foreground">
                            {formatDate(login.timestamp)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No login history available</p>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                API Tokens
              </CardTitle>
              <CardDescription>Manage API tokens for programmatic access</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {security?.api_tokens?.length > 0 ? (
                <div className="space-y-2">
                  {security.api_tokens.map((t) => (
                    <div key={t.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{t.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Created: {formatDate(t.created_at)} • Last used: {t.last_used ? formatDate(t.last_used) : 'Never'}
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => setTokenToDelete(t.id)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-4">No API tokens</p>
              )}
              <Button onClick={() => setShowTokenDialog(true)} variant="outline" className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Create New Token
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Working Hours</CardTitle>
              <CardDescription>Configure your availability for scheduling</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Working Days</Label>
                <div className="flex flex-wrap gap-2">
                  {DAYS_OF_WEEK.map((day) => (
                    <Button
                      key={day.value}
                      variant={preferences?.working_days?.includes(day.value) ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        const days = preferences?.working_days || [];
                        const newDays = days.includes(day.value)
                          ? days.filter(d => d !== day.value)
                          : [...days, day.value];
                        setPreferences({...preferences, working_days: newDays});
                      }}
                    >
                      {day.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Start Time</Label>
                  <Input 
                    type="time" 
                    value={preferences?.working_hours_start || '08:00'}
                    onChange={(e) => setPreferences({...preferences, working_hours_start: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input 
                    type="time" 
                    value={preferences?.working_hours_end || '17:00'}
                    onChange={(e) => setPreferences({...preferences, working_hours_end: e.target.value})}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Appointment Defaults</CardTitle>
              <CardDescription>Default settings for new appointments</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Default Duration (minutes)</Label>
                  <Select 
                    value={String(preferences?.default_appointment_duration || 30)}
                    onValueChange={(v) => setPreferences({...preferences, default_appointment_duration: parseInt(v)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="45">45 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="90">1.5 hours</SelectItem>
                      <SelectItem value="120">2 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Default Appointment Type</Label>
                  <Select 
                    value={preferences?.default_appointment_type || 'follow_up'}
                    onValueChange={(v) => setPreferences({...preferences, default_appointment_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {APPOINTMENT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Buffer Time (minutes)</Label>
                  <Select 
                    value={String(preferences?.scheduling_buffer || 15)}
                    onValueChange={(v) => setPreferences({...preferences, scheduling_buffer: parseInt(v)})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">No buffer</SelectItem>
                      <SelectItem value="5">5 minutes</SelectItem>
                      <SelectItem value="10">10 minutes</SelectItem>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Calendar Display</CardTitle>
              <CardDescription>Customize how your calendar appears</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Calendar Color</Label>
                <div className="flex gap-2">
                  {CALENDAR_COLORS.map(color => (
                    <button
                      key={color}
                      className={cn(
                        "w-8 h-8 rounded-full border-2 transition-all",
                        preferences?.calendar_color === color ? "border-foreground scale-110" : "border-transparent"
                      )}
                      style={{ backgroundColor: color }}
                      onClick={() => setPreferences({...preferences, calendar_color: color})}
                    />
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Show Weekends</Label>
                  <p className="text-sm text-muted-foreground">Display Saturday and Sunday on calendar</p>
                </div>
                <Switch 
                  checked={preferences?.show_weekends ?? true}
                  onCheckedChange={(checked) => setPreferences({...preferences, show_weekends: checked})}
                />
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleUpdatePreferences} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Calendar Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Regional Tab */}
        <TabsContent value="regional" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Time & Date</CardTitle>
              <CardDescription>Configure how dates and times are displayed</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Timezone</Label>
                  <Select 
                    value={preferences?.timezone || 'America/Chicago'}
                    onValueChange={(v) => setPreferences({...preferences, timezone: v})}
                  >
                    <SelectTrigger data-testid="timezone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Date Format</Label>
                  <Select 
                    value={preferences?.date_format || 'MM/DD/YYYY'}
                    onValueChange={(v) => setPreferences({...preferences, date_format: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DATE_FORMATS.map(fmt => (
                        <SelectItem key={fmt.value} value={fmt.value}>{fmt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Time Format</Label>
                  <Select 
                    value={preferences?.time_format || '12h'}
                    onValueChange={(v) => setPreferences({...preferences, time_format: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_FORMATS.map(fmt => (
                        <SelectItem key={fmt.value} value={fmt.value}>{fmt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Language & Locale</CardTitle>
              <CardDescription>Set your preferred language and regional formats</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Language</Label>
                  <Select 
                    value={preferences?.language || 'en'}
                    onValueChange={(v) => setPreferences({...preferences, language: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map(lang => (
                        <SelectItem key={lang.value} value={lang.value}>{lang.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Number Format</Label>
                  <Select 
                    value={preferences?.number_format || 'en-US'}
                    onValueChange={(v) => setPreferences({...preferences, number_format: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en-US">1,234.56 (US)</SelectItem>
                      <SelectItem value="de-DE">1.234,56 (European)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select 
                    value={preferences?.currency || 'USD'}
                    onValueChange={(v) => setPreferences({...preferences, currency: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCIES.map(curr => (
                        <SelectItem key={curr.value} value={curr.value}>{curr.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Measurement Units</Label>
                  <Select 
                    value={preferences?.measurement_units || 'imperial'}
                    onValueChange={(v) => setPreferences({...preferences, measurement_units: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MEASUREMENT_UNITS.map(unit => (
                        <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button onClick={handleUpdatePreferences} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Regional Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Password Change Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!profile?.must_reset_password && (
              <div className="space-y-2">
                <Label>Current Password</Label>
                <div className="relative">
                  <Input 
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                    data-testid="current-password"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0"
                    onClick={() => setShowPasswords({...showPasswords, current: !showPasswords.current})}
                  >
                    {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label>New Password</Label>
              <div className="relative">
                <Input 
                  type={showPasswords.new ? "text" : "password"}
                  value={passwordForm.new_password}
                  onChange={(e) => {
                    setPasswordForm({...passwordForm, new_password: e.target.value});
                    setPasswordErrors(validatePassword(e.target.value));
                  }}
                  data-testid="new-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0"
                  onClick={() => setShowPasswords({...showPasswords, new: !showPasswords.new})}
                >
                  {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              {passwordErrors.length > 0 && (
                <div className="text-xs space-y-1">
                  {['At least 8 characters', 'At least 1 uppercase letter', 'At least 1 number', 'At least 1 special character'].map(req => {
                    const met = !passwordErrors.includes(req);
                    return (
                      <div key={req} className={cn("flex items-center gap-1", met ? "text-green-600" : "text-muted-foreground")}>
                        {met ? <Check className="w-3 h-3" /> : <div className="w-3 h-3" />}
                        {req}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <div className="relative">
                <Input 
                  type={showPasswords.confirm ? "text" : "password"}
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                  data-testid="confirm-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0"
                  onClick={() => setShowPasswords({...showPasswords, confirm: !showPasswords.confirm})}
                >
                  {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>Cancel</Button>
            <Button onClick={handlePasswordChange} disabled={saving || passwordErrors.length > 0}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Change Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Token Dialog */}
      <Dialog open={showTokenDialog} onOpenChange={(open) => {
        setShowTokenDialog(open);
        if (!open) {
          setCreatedToken(null);
          setNewTokenName('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{createdToken ? 'Token Created' : 'Create API Token'}</DialogTitle>
            <DialogDescription>
              {createdToken 
                ? 'Copy your token now. You won\'t be able to see it again.'
                : 'Create a token for programmatic API access.'}
            </DialogDescription>
          </DialogHeader>
          {createdToken ? (
            <div className="space-y-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Your API Token:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm break-all">{createdToken.token}</code>
                  <Button size="icon" variant="ghost" onClick={() => copyToClipboard(createdToken.token)}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-yellow-800 dark:text-yellow-200">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p className="text-sm">Save this token securely. It will not be shown again.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Token Name</Label>
                <Input 
                  value={newTokenName}
                  onChange={(e) => setNewTokenName(e.target.value)}
                  placeholder="e.g., Integration API Key"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            {createdToken ? (
              <Button onClick={() => { setShowTokenDialog(false); setCreatedToken(null); }}>
                Done
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowTokenDialog(false)}>Cancel</Button>
                <Button onClick={handleCreateToken} disabled={saving || !newTokenName.trim()}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Create Token
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Token Confirmation */}
      <AlertDialog open={!!tokenToDelete} onOpenChange={(open) => !open && setTokenToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API Token?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. Any applications using this token will lose access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteToken} className="bg-destructive text-destructive-foreground">
              Revoke Token
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProfilePage;
